import { DraftItem, DraftLike } from "src/app/models/models";
import { mergeDraftFrontendFieldsFromLocal } from "./draft-frontend-fields.utils";

export type StagingPullDecision =
	| { action: "keep_local"; draft: DraftItem; shouldSync: boolean }
	| { action: "use_server"; draft: DraftItem };

export function draftTimestampMs(draft: Pick<DraftItem, "lastUpdatedDate"> | undefined): number {
	return new Date(draft?.lastUpdatedDate || 0).getTime();
}

/** True when local lastUpdatedDate is strictly newer than server. */
export function isLocalDraftNewer(local: DraftItem, server: DraftItem): boolean {
	return draftTimestampMs(local) > draftTimestampMs(server);
}

/**
 * Resolve a single draft present on both local IndexedDB and the staging API.
 * Mirrors `refreshDraftFromApi`: newer local wins (and should be pushed); otherwise
 * server is base with frontend-only v3 fields merged from local.
 */
export function resolveStagingPullConflict(
	serverDraft: DraftItem,
	localDraft: DraftItem | undefined,
): StagingPullDecision {
	if (localDraft && isLocalDraftNewer(localDraft, serverDraft)) {
		return {
			action: "keep_local",
			draft: localDraft,
			shouldSync: localDraft.synced !== true,
		};
	}

	return {
		action: "use_server",
		draft: mergeDraftFrontendFieldsFromLocal(
			{
				...serverDraft,
				synced: true,
			},
			localDraft,
		),
	};
}

/**
 * Local drafts that must survive a clear-and-repopulate from the server list:
 * - not present on server, and
 * - unsynced (never pushed / last push failed), or soft-deleted (offline delete queue).
 *
 * Synced local-only drafts are intentionally dropped (completed / remote sign-off
 * drafts that no longer appear in instructor staging).
 */
export function collectLocalOnlyDraftsToPreserve(
	localDrafts: DraftItem[],
	serverIds: Set<string>,
): DraftItem[] {
	return (localDrafts ?? []).filter((draft) => {
		if (!draft?.id || serverIds.has(draft.id)) {
			return false;
		}
		const deleted = (draft as DraftLike).deleted === true;
		return deleted || draft.synced !== true;
	});
}

/** Synced local rows absent from the merged write set — safe to remove after a successful staging pull. */
export function collectStaleSyncedDraftIdsToRemove(
	localDrafts: DraftItem[],
	draftsToWriteIds: Set<string>,
): string[] {
	return (localDrafts ?? [])
		.filter((draft) => {
			if (!draft?.id || draftsToWriteIds.has(draft.id)) {
				return false;
			}
			if ((draft as DraftLike).deleted === true) {
				return false;
			}
			return draft.synced === true;
		})
		.map((draft) => draft.id);
}
